import { globalEventBus, GameEvents } from './EventBus.js';
import { i18n } from '../utils/I18n.js';

export const AchievementEvents = {
    ACHIEVEMENT_UNLOCKED: 'achievement:unlocked'
};

export class AchievementSystem {
    constructor() {
        this.events = globalEventBus;
        this.storageKey = 'jigsaw_achievements';
        this.statsKey = 'jigsaw_stats';
        
        // Load data
        this.unlocked = this.loadData(this.storageKey);
        this.stats = this.loadData(this.statsKey) || {
            totalWins: 0,
            totalPlayTime: 0, // seconds
            fastestWin: Infinity
        };

        // Define achievements
        this.achievements = [
            // --- Progression ---
            {
                id: 'first_win',
                titleKey: 'achievement.first_win_title',
                descKey: 'achievement.first_win_desc',
                icon: '🧩',
                condition: (state, config, stats) => stats.totalWins >= 1
            },
            {
                id: 'novice_puzzler',
                titleKey: 'achievement.novice_puzzler_title',
                descKey: 'achievement.novice_puzzler_desc',
                icon: '🥉',
                condition: (state, config, stats) => stats.totalWins >= 5
            },
            {
                id: 'puzzle_enthusiast',
                titleKey: 'achievement.puzzle_enthusiast_title',
                descKey: 'achievement.puzzle_enthusiast_desc',
                icon: '🥈',
                condition: (state, config, stats) => stats.totalWins >= 20
            },
             {
                id: 'master_collector',
                titleKey: 'achievement.master_collector_title',
                descKey: 'achievement.master_collector_desc',
                icon: '🥇',
                condition: (state, config, stats) => stats.totalWins >= 50
            },

            // --- Speed ---
            {
                id: 'speed_runner',
                titleKey: 'achievement.speed_runner_title',
                descKey: 'achievement.speed_runner_desc',
                icon: '⚡',
                condition: (state) => state.time <= 180
            },
            {
                id: 'lightning_fast',
                titleKey: 'achievement.lightning_fast_title',
                descKey: 'achievement.lightning_fast_desc',
                icon: '🌩️',
                condition: (state) => state.time <= 60
            },
            {
                id: 'marathon',
                titleKey: 'achievement.marathon_title',
                descKey: 'achievement.marathon_desc',
                icon: '🐢',
                condition: (state) => state.time >= 600
            },

            // --- Difficulty ---
            {
                id: 'tiny_steps',
                titleKey: 'achievement.tiny_steps_title',
                descKey: 'achievement.tiny_steps_desc',
                icon: '🔍',
                condition: (state, config) => (config.cols * config.rows) <= 25
            },
            {
                id: 'puzzle_master_hard',
                titleKey: 'achievement.puzzle_master_title',
                descKey: 'achievement.puzzle_master_desc',
                icon: '🏆',
                condition: (state, config) => (config.cols * config.rows) >= 100
            },
            {
                id: 'insane_dedication',
                titleKey: 'achievement.insane_dedication_title',
                descKey: 'achievement.insane_dedication_desc',
                icon: '🤯',
                condition: (state, config) => (config.cols * config.rows) >= 200
            },

            // --- Gameplay ---
            {
                id: 'precision_master',
                titleKey: 'achievement.precision_master_title',
                descKey: 'achievement.precision_master_desc',
                icon: '🎯',
                condition: (state, config) =>
                    config.rotation &&
                    config.rotation.enabled &&
                    state.stats.rotationCount === 0
            },
            {
                id: 'high_scorer',
                titleKey: 'achievement.high_scorer_title',
                descKey: 'achievement.high_scorer_desc',
                icon: '⭐',
                condition: (state) => state.score >= 1000
            },
             {
                id: 'legendary_score',
                titleKey: 'achievement.legendary_score_title',
                descKey: 'achievement.legendary_score_desc',
                icon: '🌟',
                condition: (state) => state.score >= 5000
            }
        ];

        this.bindEvents();
    }

    loadData(key) {
        try {
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : {};
        } catch (e) {
            console.error(`Failed to load ${key}`, e);
            return {};
        }
    }

    saveData(key, data) {
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (e) {
            console.error(`Failed to save ${key}`, e);
        }
    }

    bindEvents() {
        this.events.on(GameEvents.GAME_COMPLETE, () => this.handleGameComplete());
    }

    handleGameComplete() {
        const game = window.__PUZZLE_GAME__;
        if (!game) return;

        const state = game.getState();
        const config = game.config.puzzle; // Effective puzzle config
        // Need full config for rotation check
        const fullConfig = game.config;

        // Update stats
        this.updateStats(state);

        // Check achievements
        this.checkAchievements(state, fullConfig);
    }

    updateStats(state) {
        this.stats.totalWins = (this.stats.totalWins || 0) + 1;
        this.stats.totalPlayTime = (this.stats.totalPlayTime || 0) + state.time;
        
        if (state.time < this.stats.fastestWin) {
            this.stats.fastestWin = state.time;
        }

        this.saveData(this.statsKey, this.stats);
    }

    checkAchievements(state, fullConfig) {
        // Merge puzzle config with general config for convenience in conditions
        const combinedConfig = { ...fullConfig.puzzle, rotation: fullConfig.rotation };

        this.achievements.forEach(achievement => {
            if (this.unlocked[achievement.id]) return; // Already unlocked

            if (achievement.condition(state, combinedConfig, this.stats)) {
                this.unlock(achievement);
            }
        });
    }

    unlock(achievement) {
        this.unlocked[achievement.id] = {
            timestamp: Date.now()
        };
        this.saveData(this.storageKey, this.unlocked);

        const translated = {
            ...achievement,
            title: i18n.t(achievement.titleKey),
            description: i18n.t(achievement.descKey)
        };
        console.log(`Achievement Unlocked: ${translated.title}`);
        this.events.emit(AchievementEvents.ACHIEVEMENT_UNLOCKED, translated);
    }
    
    getAchievements() {
        return this.achievements.map(a => ({
            ...a,
            title: i18n.t(a.titleKey),
            description: i18n.t(a.descKey),
            unlocked: !!this.unlocked[a.id],
            unlockedAt: this.unlocked[a.id]?.timestamp
        }));
    }

    getStats() {
        return this.stats;
    }
}